import { FileText, TrendingDown, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import ServicePageLayout from "../components/ServicePageLayout";

const prestations = [
    {
        icon: FileText,
        title: "Déclarations d'impôts",
        desc: "Établissement des déclarations fiscales pour particuliers, indépendants et sociétés. Respect des délais, dossier complet.",
    },
    {
        icon: TrendingDown,
        title: "Optimisation fiscale légale",
        desc: "Analyse de votre situation pour identifier les possibilités d'optimisation dans le cadre strict de la loi suisse.",
    },
    {
        icon: Shield,
        title: "Représentation fiscale",
        desc: "Fiduciaire Bleich vous représente auprès des autorités fiscales genevoises en cas de contrôle ou de demande de renseignements.",
    },
];

const etapes = [
    {
        num: "01",
        title: "Premier entretien gratuit",
        desc: "Pour analyser votre situation fiscale.",
    },
    {
        num: "02",
        title: "Collecte des documents",
        desc: "Établissement de votre déclaration ou de votre dossier.",
    },
    {
        num: "03",
        title: "Dépôt et suivi",
        desc: "Dépôt dans les délais, suivi des décisions de taxation et conseil pour l'avenir.",
    },
];

const pourquoiCols = [
    {
        title: "Expertise locale",
        desc: "Fiduciaire Bleich connaît en profondeur la pratique des autorités fiscales genevoises et les spécificités du droit cantonal.",
    },
    {
        title: "Approche globale",
        desc: (
            <>
                Fiscalité,{" "}
                <Link
                    to="/comptabilite"
                    data-testid="link-comptabilite"
                    className="text-[#1C9CAC] hover:underline"
                >
                    comptabilité
                </Link>{" "}
                et{" "}
                <Link
                    to="/tva"
                    data-testid="link-tva"
                    className="text-[#1C9CAC] hover:underline"
                >
                    TVA
                </Link>{" "}
                sont traitées conjointement. Votre situation est analysée dans sa globalité, pas par compartiments.
            </>
        ),
    },
    {
        title: "Présents depuis 2004",
        desc: "Plus de vingt ans d'accompagnement fiscal à Genève, pour une clientèle locale et internationale.",
    },
];

const faqs = [
    {
        q: "Quand faut-il déposer sa déclaration d'impôts à Genève ?",
        a: "Les particuliers déposent leur déclaration au cours de l'année qui suit l'année fiscale, avec la possibilité de demander une prolongation de délai. Fiduciaire Bleich gère ces échéances pour vous et veille au respect des délais.",
    },
    {
        q: "Qu'est-ce que l'optimisation fiscale légale ?",
        a: "Il s'agit d'organiser votre situation pour réduire votre charge fiscale en utilisant uniquement les possibilités prévues par la loi, par exemple les déductions et les choix de structure autorisés. Fiduciaire Bleich agit toujours dans le cadre strict du droit suisse.",
    },
    {
        q: "Une société est-elle imposée deux fois en Suisse ?",
        a: "Le bénéfice de la société est imposé au niveau de la société, puis les dividendes versés sont imposés chez l'actionnaire. Des allègements existent dans certains cas. Fiduciaire Bleich analyse l'impact réel selon votre structure.",
    },
    {
        q: "Fiduciaire Bleich peut-elle me représenter face à l'administration fiscale ?",
        a: "Oui. Fiduciaire Bleich vous représente auprès des autorités fiscales genevoises, notamment en cas de contrôle ou de demande de renseignements.",
    },
];

const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Fiscalité à Genève",
    serviceType: "Conseil fiscal et établissement des déclarations d'impôts",
    provider: {
        "@type": "AccountingService",
        name: "Fiduciaire Bleich Sàrl",
        url: "https://fiduciaire-bleich.ch",
    },
    areaServed: { "@type": "AdministrativeArea", name: "Canton de Genève" },
    url: "https://fiduciaire-bleich.ch/fiscalite",
};

const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 2, name: "Nos services", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 3, name: "Fiscalité", item: "https://fiduciaire-bleich.ch/fiscalite" },
    ],
};

const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

const Fiscalite = () => {
    return (
        <ServicePageLayout
            testIdPrefix="fiscalite"
            seo={{
                title: "Fiscalité à Genève, conseil fiscal | Fiduciaire Bleich",
                description:
                    "Conseil fiscal pour particuliers et sociétés à Genève. Déclarations d'impôts, optimisation légale, représentation fiscale. Fiduciaire Bleich depuis 2004. Premier entretien gratuit.",
                canonical: "https://fiduciaire-bleich.ch/fiscalite",
            }}
            jsonLd={{ service: serviceJsonLd, breadcrumb: breadcrumbJsonLd, faq: faqJsonLd }}
            breadcrumb="Fiscalité"
            hero={{
                eyebrow: "Nos services",
                h1Prefix: "Fiscalité à",
                h1Italic: "Genève.",
                subtitle: "Un conseil fiscal précis, pour particuliers et sociétés.",
                intro:
                    "La fiscalité suisse est complexe et en constante évolution. Fiduciaire Bleich accompagne ses clients dans toutes leurs démarches fiscales, de la déclaration annuelle à la planification à long terme, avec rigueur et dans le cadre légal.",
            }}
            prestations={prestations}
            prestationsHeading={
                <>
                    Ce que <span className="italic">nous faisons.</span>
                </>
            }
            etapes={etapes}
            processHeading={
                <>
                    Une démarche <span className="italic">structurée.</span>
                </>
            }
            pourquoiHeading={
                <>
                    Un conseil fiscal
                    <br />
                    de <span className="italic">confiance à Genève.</span>
                </>
            }
            pourquoiCols={pourquoiCols}
            faqHeading={
                <>
                    Questions fréquentes
                    <br />
                    sur la <span className="italic">fiscalité.</span>
                </>
            }
            faqs={faqs}
            cta={{
                heading: (
                    <>
                        Parlons de votre <span className="italic">situation fiscale.</span>
                    </>
                ),
                text: "Un premier entretien gratuit et confidentiel permet d'évaluer vos enjeux fiscaux et la manière dont Fiduciaire Bleich peut vous accompagner. Sans engagement.",
            }}
        />
    );
};

export default Fiscalite;
